// src/helpers/ticketTokenVerifier.ts
// Xác thực + giải mã token QR ký ở server, KHÔNG gửi gì lên server để verify — chỉ cần PUBLIC KEY
// (an toàn khi để công khai trong bundle FE, vì public key chỉ dùng để XÁC THỰC chữ ký, không thể
// tự dùng để sinh ra token hợp lệ mới). Dùng Web Crypto API có sẵn trên trình duyệt, không thêm thư
// viện JWT ngoài.
//
// Vé phát hành hiện tại dùng token ECDSA P-256 dạng nhị phân gọn (payload đóng gói thủ công +
// 64 byte chữ ký, xem TicketTokenSigner bên ticket-service) — thay cho JWT RS256 cũ (RSA-2048, token
// 500+ ký tự) để QR ngắn lại còn ~100-140 ký tự mà vẫn tự xác thực offline được. Vé in TRƯỚC 2 lần
// đổi định dạng (cũ nhất: AES-256-GCM qua QrTokenCodec, giữa: JWT RS256) vẫn phải quét được nên vẫn
// giữ nguyên các đường xử lý cũ — không xóa.
//
// Phân biệt 3 định dạng của `token`:
//  1. JWT RS256 (2 dấu chấm)      → verify bằng RSA public key (đường cũ, giữ nguyên).
//  2. Nhị phân gọn ECDSA (không dấu chấm) → thử verify bằng EC public key trước.
//  3. AES-256-GCM cũ (không dấu chấm, verify ECDSA thất bại) → rơi xuống gọi API backend
//     (TicketLookup/index.tsx tự làm việc này khi hàm dưới đây trả về null).

// Public key RSA (SPKI, PEM) — tương ứng private key RSA cũ, chỉ còn dùng để verify vé in TRƯỚC khi
// đổi sang ECDSA. Không dùng cho vé phát hành mới.
const RSA_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA7HtGkNeMpGWAPxNw8L6u
4wwLqVBq+TDpNxeFLdefhKTVOsB+sOx/mMf8ChG0HZzHCleujoqAm8mTMusauj9F
9qHuc2Y4ygmlecY0X6l+hlErcOk/9uXS2pRiNWQzpkE0UunTDhPaRUL6K5zP6pU7
uPFTBo2lrYB6TKpq8Oewt0PvMaTFM6qBQnzHtlbygtz/6zEHdCu3A3MPBTND4KQN
rSDJvvVI/v23Co3+xfUaVa4iLPIJhE5dOcFpfj3VmWN4T93uwSkliKL/Ez7jLFjE
jFRXJ66y694KXBuswjB1LSMSMZKb+ctqk0rUrnXtJ/h0on580xRKmu8TaE1eqtpQ
IwIDAQAB
-----END PUBLIC KEY-----`;

// Public key EC P-256 (SPKI, PEM) — tương ứng private key TicketSigning:PrivateKeyPem hiện tại bên
// ticket-service (TicketTokenSigner, ECDSA) — PHẢI khớp đúng cặp khóa, đổi 1 bên thì bên kia không
// xác thực được nữa.
const EC_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE/KHtx3lx0LzjFp9+ZkO7V87fgDL/
Dh0NaoyAeAcOiybv41tZt89W766m8iLEox6pEOCiEHOFNRkGALd8EfUIdg==
-----END PUBLIC KEY-----`;

// Chu ky ECDSA P-256 raw (r||s, IEEE P1363) luon dai 64 byte — khop dinh dang
// DSASignatureFormat.IeeeP1363FixedFieldConcatenation ben TicketTokenSigner.cs.
const EC_SIGNATURE_SIZE = 64;
const EC_MIN_PAYLOAD_SIZE = 4 + 1 + 5 + 1 + 1 + 4;

export interface DecodedTicketToken {
  lotteryPeriodId: number;
  ticketNumber: string;
  symbol: string;
  planCode: string;
  expectedDrawDate: string; // ISO date string
}

let rsaPublicKeyPromise: Promise<CryptoKey> | null = null;
let ecPublicKeyPromise: Promise<CryptoKey> | null = null;

const base64UrlToBytes = (base64url: string): Uint8Array => {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const base64 = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const pemToArrayBuffer = (pem: string): ArrayBuffer => {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

const getRsaPublicKey = (): Promise<CryptoKey> => {
  if (!rsaPublicKeyPromise) {
    rsaPublicKeyPromise = window.crypto.subtle.importKey(
      'spki',
      pemToArrayBuffer(RSA_PUBLIC_KEY_PEM),
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['verify'],
    );
  }
  return rsaPublicKeyPromise;
};

const getEcPublicKey = (): Promise<CryptoKey> => {
  if (!ecPublicKeyPromise) {
    ecPublicKeyPromise = window.crypto.subtle.importKey(
      'spki',
      pemToArrayBuffer(EC_PUBLIC_KEY_PEM),
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );
  }
  return ecPublicKeyPromise;
};

// Phan biet token JWT (3 phan ngan cach boi dau cham) voi 2 dinh dang con lai (1 chuoi base64url
// lien tuc, khong co dau cham): nhi phan gon ECDSA (hien tai) va AES-256-GCM cu.
export const isJwtFormatToken = (token: string): boolean => token.split('.').length === 3;

const readUInt32LE = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] |
    (bytes[offset + 1] << 8) |
    (bytes[offset + 2] << 16) |
    bytes[offset + 3] * 0x1000000) >>> 0;

// TicketNumber toi da 10 chu so (~9.99 ty) nen can 5 byte — khong dung bitwise shift (JS bitwise chi
// an toan trong 32 bit) ma cong don theo luy thua 256 de giu chinh xac toi Number.MAX_SAFE_INTEGER.
const readUInt40LE = (bytes: Uint8Array, offset: number): number => {
  let value = 0;
  for (let i = 4; i >= 0; i -= 1) value = value * 256 + bytes[offset + i];
  return value;
};

interface CompactTokenPayload {
  lotteryPeriodId: number;
  ticketNumber: string;
  symbol: string;
  planCode: string;
  expectedDrawDateUnixSeconds: number;
}

// Giai ma phan payload nhi phan gon — PHAI khop dung thu tu truong voi
// TicketTokenSigner.PackPayload/TryUnpackPayload ben ticket-service.
const unpackCompactPayload = (data: Uint8Array): CompactTokenPayload | null => {
  if (data.length < EC_MIN_PAYLOAD_SIZE) return null;

  let offset = 0;
  const lotteryPeriodId = readUInt32LE(data, offset);
  offset += 4;

  const ticketDigits = data[offset];
  offset += 1;
  if (ticketDigits === 0 || ticketDigits > 10) return null;

  const ticketNumberValue = readUInt40LE(data, offset);
  offset += 5;

  if (offset >= data.length) return null;
  const symbolLen = data[offset];
  offset += 1;
  if (offset + symbolLen > data.length) return null;
  const symbol = new TextDecoder().decode(data.slice(offset, offset + symbolLen));
  offset += symbolLen;

  if (offset >= data.length) return null;
  const planLen = data[offset];
  offset += 1;
  if (offset + planLen + 4 > data.length) return null;
  const planCode = new TextDecoder().decode(data.slice(offset, offset + planLen));
  offset += planLen;

  const expectedDrawDateUnixSeconds = readUInt32LE(data, offset);

  return {
    lotteryPeriodId,
    ticketNumber: String(ticketNumberValue).padStart(ticketDigits, '0'),
    symbol,
    planCode,
    expectedDrawDateUnixSeconds,
  };
};

// Xac thuc chu ky ECDSA P-256 + giai ma payload nhi phan gon — dinh dang token hien tai cho ve moi
// phat hanh. Tra ve null neu khong phai dinh dang nay hoac chu ky khong khop (goi noi tiep bang AES-
// 256-GCM ben duoi cho ve in truoc do).
const verifyAndDecodeCompactToken = async (token: string): Promise<DecodedTicketToken | null> => {
  let data: Uint8Array;
  try {
    data = base64UrlToBytes(token);
  } catch {
    return null;
  }

  if (data.length <= EC_SIGNATURE_SIZE) return null;

  const payload = data.slice(0, data.length - EC_SIGNATURE_SIZE);
  const signature = data.slice(data.length - EC_SIGNATURE_SIZE);

  try {
    const publicKey = await getEcPublicKey();
    const isValid = await window.crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      publicKey,
      signature,
      payload,
    );
    if (!isValid) return null;

    const decoded = unpackCompactPayload(payload);
    if (!decoded) return null;

    return {
      lotteryPeriodId: decoded.lotteryPeriodId,
      ticketNumber: decoded.ticketNumber,
      symbol: decoded.symbol,
      planCode: decoded.planCode,
      expectedDrawDate: new Date(decoded.expectedDrawDateUnixSeconds * 1000).toISOString(),
    };
  } catch {
    return null;
  }
};

// Xac thuc chu ky JWT RS256 (RSASSA-PKCS1-v1_5 + SHA-256) — dinh dang cu, chi con dung cho ve in
// truoc khi doi sang ECDSA nhi phan gon.
const verifyAndDecodeJwtToken = async (token: string): Promise<DecodedTicketToken | null> => {
  const [headerB64, payloadB64, signatureB64] = token.split('.');

  try {
    const publicKey = await getRsaPublicKey();
    const signingInput = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
    const signatureBytes = base64UrlToBytes(signatureB64);

    const isValid = await window.crypto.subtle.verify(
      { name: 'RSASSA-PKCS1-v1_5' },
      publicKey,
      signatureBytes,
      signingInput,
    );
    if (!isValid) return null;

    const payloadJson = new TextDecoder().decode(base64UrlToBytes(payloadB64));
    const payload = JSON.parse(payloadJson);

    const lotteryPeriodId = Number(payload.ky);
    const ticketNumber = payload.so ? String(payload.so) : '';
    const symbol = payload.ky_hieu ? String(payload.ky_hieu) : '';
    const planCode = payload.ma_ky ? String(payload.ma_ky) : '';
    const expectedDrawDateUnixSeconds = Number(payload.ngay_mo);

    if (!lotteryPeriodId || !ticketNumber || !symbol || !expectedDrawDateUnixSeconds) return null;

    return {
      lotteryPeriodId,
      ticketNumber,
      symbol,
      planCode,
      expectedDrawDate: new Date(expectedDrawDateUnixSeconds * 1000).toISOString(),
    };
  } catch {
    return null;
  }
};

// Diem vao duy nhat cho man hinh tra cuu — tu nhan dien dinh dang token va xac thuc offline (khong
// goi API) khi co the. Tra ve null neu token khong phai JWT/ECDSA hop le (VD dinh dang AES-256-GCM
// cu) de caller (TicketLookup/index.tsx) roi xuong goi API backend.
export const verifyAndDecodeTicketToken = async (token: string): Promise<DecodedTicketToken | null> => {
  if (isJwtFormatToken(token)) return verifyAndDecodeJwtToken(token);
  return verifyAndDecodeCompactToken(token);
};

// Muc 12, README.md — camera QR tra ve nguyen van chuoi da giai ma tu anh QR, thuong la 1 URL day du
// in tren ve (VD https://lms.exo.io.vn/v/{token}) nhung cung co the la token tran (thiet bi/man hinh
// khac). Lay doan cuoi cung cua duong dan lam token — dung chung cho moi man hinh can quet QR ve (thu
// hoi, giao ve...) thay vi moi noi tu viet lai logic tach token nhu TicketLookup/index.tsx da lam
// rieng (parseLookupTarget, chi phuc vu dieu huong nen tra ve ca duong dan, khong phai token tran).
export const extractTicketTokenFromScan = (raw: string): string => {
  const trimmed = raw.trim();
  try {
    const parsed = new URL(trimmed);
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : trimmed;
  } catch {
    return trimmed;
  }
};
